import { ReactNode } from 'react';
import { Box, Typography } from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import { DISPLAY_FONT, RADIUS } from '../../../../components/ui/tokens';
import { ink } from '../../../mcp/SignalDesk';

export type CalloutTone = 'primary' | 'success' | 'warning' | 'error';

interface SimulateCalloutProps {
  tone: CalloutTone;
  icon: ReactNode;
  title: ReactNode;
  caption?: ReactNode;
  /** Right-hand content (a badge or actions); wraps below on narrow screens. */
  aside?: ReactNode;
  /** Smaller title and icon for secondary prompts that sit under the verdict. */
  compact?: boolean;
}

/** Tinted panel with an icon tile: the result verdict and the save-mapping prompt. */
export const SimulateCallout = ({ tone, icon, title, caption, aside, compact }: SimulateCalloutProps) => {
  const theme = useTheme();
  const dark = theme.palette.mode === 'dark';
  const color = theme.palette[tone].main;
  const tile = compact ? 34 : 40;

  return (
    <Box
      role="status"
      sx={{
        display: 'flex',
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: 1.5,
        p: 2,
        borderRadius: RADIUS.card,
        border: `1px solid ${alpha(color, dark ? 0.35 : 0.25)}`,
        backgroundColor: alpha(color, dark ? 0.1 : 0.06)
      }}
    >
      <Box
        sx={{
          width: tile,
          height: tile,
          flexShrink: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: compact ? 16 : 20,
          borderRadius: RADIUS.control,
          color: ink(theme, tone),
          backgroundColor: alpha(color, dark ? 0.18 : 0.12)
        }}
      >
        {icon}
      </Box>
      <Box sx={{ flex: 1, minWidth: 180 }}>
        <Typography
          component="div"
          sx={{ fontFamily: DISPLAY_FONT, fontSize: compact ? 15 : 17, fontWeight: 700, letterSpacing: '-0.01em' }}
        >
          {title}
        </Typography>
        {caption && (
          <Typography component="div" variant="body2" color="text.secondary">
            {caption}
          </Typography>
        )}
      </Box>
      {aside && <Box sx={{ flexShrink: 0, ml: 'auto' }}>{aside}</Box>}
    </Box>
  );
};
